import { scrollToSection } from '../lib/scroll';
import { useStore } from '../state/store';
import { HideToggle } from './HideToggle';

export function Header({ onOpenSettings, onOpenDetails }: {
  onOpenSettings: () => void;
  /** Opens the value card in full. Only the Value screen passes it, because
      only that screen has the short card the details belong to. */
  onOpenDetails?: () => void;
}) {
  const { book, state, alerts, saveSettings, refresh } = useStore();
  const critical = alerts.filter((a) => a.severity === 'critical').length;
  const { label } = book.session;
  const live = label === 'OPEN';
  const ccy = state.settings.displayCurrency;

  const appName = state.settings.appName?.trim() || 'Position Book';
  const avatar = state.settings.avatar;
  /* Initials from whatever it is called, so a renamed book does not keep
     someone else's monogram in the corner. */
  const initials = appName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('') || 'PB';

  return (
    <header className="app-header">
      <button className="avatar" onClick={onOpenSettings} aria-label="Settings"
              data-photo={avatar ? 'true' : undefined}>
        {avatar ? <img src={avatar} alt="" /> : initials}
      </button>
      <span className="app-name">{appName}</span>

      {/* The dot is decorative; the badge next to it carries the same state in
          words, so colour is never the only signal (§11). */}
      <span className="live-dot" data-live={live} aria-hidden="true" />
      <span className="badge" data-session={label}>
        {state.refreshing ? 'UPDATING' : label}
      </span>

      {/* The cadence in §3.4 is what keeps the marks current; this is for the
          moment you want to know *now* rather than at the next tick. Same call
          the timer makes, so there is no second refresh path to keep in step. */}
      <button className="refresh-btn" data-busy={state.refreshing ? 'true' : undefined}
              disabled={state.refreshing} aria-label="Refresh the marks now"
              onClick={() => void refresh({ full: true })}>
        <span aria-hidden="true">↻</span>
      </button>

      {/* Covering the figures is something you do because of who just walked up,
          so it is one tap from wherever you are. The same control is in every
          other screen's top bar — see components/HideToggle.tsx. */}
      <HideToggle />

      {/* Bars: the way into money paid in, the comparisons and results, which
          the card on the Value screen leaves out to stay a glance. Same round
          control as refresh and the eye beside it. */}
      {onOpenDetails ? (
        <button className="refresh-btn" onClick={onOpenDetails}
                aria-label="Value in detail: money paid in, comparisons and results">
          <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 16V10M10 16V4M16 16V8" />
          </svg>
        </button>
      ) : null}

      {/* The alert cards sit at the foot of the page, out of the way. A breach
          still cannot be five screens from anywhere you might be looking, so the
          count rides in the header and jumps you there. */}
      {critical > 0 ? (
        <button
          className="alert-pip"
          aria-label={`${critical} limit${critical === 1 ? '' : 's'} breached, go to alerts`}
          onClick={() => scrollToSection('alerts')}
        >
          <span aria-hidden="true">◆</span> {critical}
        </button>
      ) : null}

      <div className="ccy-toggle" role="group" aria-label="Display currency">
        {(['USD', 'PLN'] as const).map((c) => (
          <button
            key={c}
            aria-pressed={ccy === c}
            onClick={() => void saveSettings({ displayCurrency: c })}
          >
            {c}
          </button>
        ))}
      </div>
    </header>
  );
}
